import { useChatHistory } from '@packages/chat/react';
import { QueryKey } from '@shared/config';
import { PaginateType } from '@shared/types';
import { useId } from 'react';
import type { ListUserAiMessagesResponse, UserAiMessage } from '../types';
import { userApi } from '../userApi';

export const USER_AI_MESSAGES_PER_PAGE = 30;

// Chat history keys include the mounted chat session: its request markers
// must not mix with cached observations of an earlier session.
export const userAiMessageQueryKeys = {
  latestRoot: (userId: number | undefined) =>
    [QueryKey.LIST_USER_AI_MESSAGES_REFRESH, userId] as const,
  historyRoot: (userId: number | undefined) =>
    [QueryKey.LIST_USER_AI_MESSAGES, userId] as const,
  latest: (userId: number | undefined, sessionId: string) =>
    [
      QueryKey.LIST_USER_AI_MESSAGES_REFRESH,
      userId,
      sessionId,
      { paginate: PaginateType.DEFAULT, perPage: USER_AI_MESSAGES_PER_PAGE },
    ] as const,
  history: (userId: number | undefined, sessionId: string) =>
    [
      QueryKey.LIST_USER_AI_MESSAGES,
      userId,
      sessionId,
      { paginate: PaginateType.DEFAULT, perPage: USER_AI_MESSAGES_PER_PAGE },
    ] as const,
};

export const useListUserAiMessages = (
  userId: number | undefined,
  pollInterval: number | false = 10_000
) => {
  const sessionId = useId();
  const history = useChatHistory<UserAiMessage, ListUserAiMessagesResponse>({
    sessionId,
    queryKeys: {
      latest: userAiMessageQueryKeys.latest(userId, sessionId),
      history: userAiMessageQueryKeys.history(userId, sessionId),
    },
    enabled: userId != null,
    pollInterval,
    fetchPage: (page, signal) => {
      if (userId == null) {
        throw new Error('A user is required to load the conversation');
      }
      return userApi.listUserAiMessages(
        String(userId),
        {
          filter: {},
          paginate: PaginateType.DEFAULT,
          perPage: USER_AI_MESSAGES_PER_PAGE,
          page,
        },
        signal
      );
    },
  });

  return { ...history, sessionId };
};

export type UserAiMessagesHistory = ReturnType<typeof useListUserAiMessages>;
